import React from "react";
import MegaHeader from "../MegaHeader";
import { imageUrl } from "../../utils/imagePath";
import "./DFS.scss";

const DFS_IMG = (file) => imageUrl("dfs", file);
const EMBED_URL = "https://dfs-app.data-pilot.com/";

const challengeItems = [
  {
    title: "Declining revenue and profit due to inefficient product and channel mix",
    image: "challenge-revenue.svg",
  },
  {
    title: "Inability to scale high-performing products or phase out underperforming ones",
    image: "challenge-growth.svg",
  },
  {
    title: "Over-reliance on costly sales channels with high fulfilment fees",
    image: "challenge-sales.svg",
  },
  {
    title: "Lack of real-time visibility into SKU and channel performance",
    image: "challenge-visibility.svg",
  },
  {
    title: "Inefficient inventory management and missed seasonal opportunities",
    image: "challenge-inventory.svg",
  },
];

const featureItems = [
  {
    title: "Product Analytics Platform",
    text: "Track SKU performance across revenue, profit, and demand in real time.",
    icon: "feature-analytics.svg",
  },
  {
    title: "Demand Forecasting",
    text: "Predict future demand patterns using AI to improve inventory planning and stock management.",
    icon: "feature-forecasting.svg",
  },
  {
    title: "Dynamic Pricing Engine",
    text: "Adjust product pricing based on demand, competition, and inventory levels.",
    icon: "feature-pricing.svg",
  },
  {
    title: "Channel Profitability Analysis",
    text: "Monitor and compare profitability across sales channels to optimise channel strategy.",
    icon: "feature-profitability.svg",
  },
];

const stackItems = [
  { name: "Kubernetes", file: "logo-kubernetes.svg" },
  { name: "Django", file: "logo-django.svg" },
  { name: "TensorFlow", file: "logo-tensorflow.svg" },
  { name: "PostgreSQL", file: "logo-postgresql.svg" },
  { name: "Docker", file: "logo-docker.svg" },
];

const impactItems = [
  "Improved revenue by aligning product offerings with customer demand",
  "Increased profit margins through dynamic pricing and cost optimisation",
  "Enhanced operational efficiency with better inventory and demand planning",
  "Reduced reliance on high-cost channels and improved channel mix",
  "Stronger decision-making through real-time performance tracking",
];

const DFS = () => {
  return (
    <>
      <MegaHeader />

      <section className="dfs-hero">
        <div
          className="dfs-hero__background"
          aria-hidden="true"
          style={{ backgroundImage: `url(${DFS_IMG("hero.svg")})` }}
        />
        <div className="dfs-hero__content">
          <h1 className="dfs-hero__title">
            AI-Powered Product &amp; Channel Optimisation Platform
          </h1>
          <p className="dfs-hero__description">
            Optimise product performance, pricing, and sales channels using
            real-time analytics and AI-driven demand forecasting.
          </p>
        </div>
      </section>

      <section className="dfs-section dfs-challenge">
        <div className="dfs-challenge__container">
          <h2 className="dfs-section__title">The Challenge</h2>
          <p className="dfs-section__intro">
            E-commerce operations rely on fragmented data and manual analysis to
            manage products and sales channels, making it difficult to identify
            profitable opportunities. These challenges limit profitability and
            prevent timely, data-driven decision-making.
          </p>

          <div className="dfs-challenge__grid">
            {challengeItems.map((item) => (
              <article key={item.title} className="dfs-challenge-card">
                <img
                  src={DFS_IMG(item.image)}
                  alt=""
                  aria-hidden="true"
                  className="dfs-challenge-card__image"
                />
                <p className="dfs-challenge-card__text">{item.title}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="dfs-section dfs-solution">
        <div className="dfs-solution__container">
          <div className="dfs-solution__header">
            <h2 className="dfs-section__title">Solution</h2>
            <p className="dfs-section__intro dfs-solution__intro">
             The platform introduces a data-driven system that combines real-time analytics, 
             AI-powered demand forecasting, and dynamic pricing to optimise product 
             performance and channel strategy. It enables teams to track performance, 
             adjust pricing, and align inventory with demand more effectively. 
            </p>
          </div>

          <div className="dfs-solution__demo-link-row">
            <a
              href={EMBED_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="dfs-solution__demo-link"
              aria-label="View DFS demo in a new tab"
            >
              <span>View Demo</span>
              <img
                src={DFS_IMG("ViewArrow.svg")}
                alt=""
                aria-hidden="true"
                className="dfs-solution__demo-link-icon"
              />
            </a>
          </div>

          <div className="dfs-solution-embed-wrapper">
            <iframe
              src={EMBED_URL}
              title="DFS Product and Channel Optimisation Demo"
              className="dfs-iframe"
              allowFullScreen
              allow="fullscreen"
              scrolling="yes"
            />
          </div>
        </div>
      </section>

      <section className="dfs-section dfs-features">
        <div className="dfs-features__container">
          <h2 className="dfs-section__title">Key Features</h2>

          <div className="dfs-features__grid">
            {featureItems.map((item) => (
              <article key={item.title} className="dfs-feature-card">
                <img
                  src={DFS_IMG(item.icon)}
                  alt=""
                  aria-hidden="true"
                  className="dfs-feature-card__icon"
                />
                <div className="dfs-feature-card__content">
                  <h3 className="dfs-feature-card__title">{item.title}</h3>
                  <p className="dfs-feature-card__text">{item.text}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="dfs-features__stack">
            {stackItems.map((item) => (
              <img
                key={item.name}
                src={DFS_IMG(item.file)}
                alt={item.name}
                className="dfs-features__stack-logo"
              />
            ))}
          </div>
        </div>
      </section>

      <section
        className="dfs-impact"
        style={{ backgroundImage: `url(${DFS_IMG("impact-bg.svg")})` }}
      >
        <div className="dfs-impact__overlay" aria-hidden="true" />
        <div className="dfs-impact__content">
          <h2 className="dfs-section__title dfs-impact__title">Impact</h2>
          <ul className="dfs-impact__list">
            {impactItems.map((item) => (
              <li key={item}>
                <img
                  src={DFS_IMG("checkmark.svg")}
                  alt=""
                  aria-hidden="true"
                  className="dfs-impact__check"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
};

export default DFS;
